import React, { useState, useRef, useEffect } from 'react'
import {
  DialogBase,
  FieldGrid,
  PrimaryCta,
  SecondaryCta,
  Input,
} from 'components'
import {
  Button,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
} from '@mui/material'
import {
  useSetCryptoTopMutation,
  errorSnack,
  useGetCryptoTopQuery,
} from 'features'
import { useDispatch } from 'react-redux'
import { SCHOOL_CYCLE_OPTIONS } from 'constants'
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useForm, Controller } from 'react-hook-form'

/**
 *
 * @param {*} _props
 * @returns
 */
export default function SetSchoolCycleCryptoLimit(_props) {
  const {
    togglerComponent: Component = { Button },
    children,
    togglerProps = {},
  } = _props

  const [schoolCycleLimitDefined, setSchoolCycleLimitDefined] = useState(false)
  const [open, setOpen] = useState(false)
  const [isBus, setIsBus] = useState(false)

  const dispatch = useDispatch()

  const { data: limitList, isLoading: isLoadingLimits } = useGetCryptoTopQuery({
    page: 1,
    limitNumber: 50,
    topAmountType: isBus ? 'b' : 'e',
  })

  function openDialog(e) {
    e.stopPropagation()
    setOpen(true)
  }
  function closeDialog() {
    setOpen(false)
    reset()
    setSchoolCycleLimitDefined(false)
  }

  const [setCryptoTop, { isLoading }] = useSetCryptoTopMutation()

  const {
    control,
    handleSubmit,
    setError,
    watch,
    formState: { errors },
    reset,
    resetField,
  } = useForm({
    defaultValues: {
      cycle: '',
      amount: '',
    },
  })

  // let cycleOptions = !isLoadingLimits && limitList ? buildCycleOptions() : []
  let cycleOptions = React.useMemo(() => {
    if (!isLoadingLimits && limitList) {
      return buildCycleOptions()
    }
    return []
  }, [limitList, isLoadingLimits])

  function buildCycleOptions() {
    let yearsWithLimitDefined = limitList.rows.map((row) => row.cycle)
    return SCHOOL_CYCLE_OPTIONS({ raw: true }).reduce((acc, year) => {
      acc.push({
        label: 'Año ' + year,
        value: year,
        disabled: yearsWithLimitDefined.includes(year),
      })
      return acc
    }, [])
  }

  function onSubmit(data) {
    const mutation = setCryptoTop({
      cycle: data.cycle,
      amount: parseInt(data.amount),
      topAmountType: isBus ? 'b' : 'e',
    }).unwrap()
    mutation
      .then((response) => {
        if (response?.amountcriptostopid) {
          setSchoolCycleLimitDefined(data.cycle)
        }
      })
      .catch((error) => {
        if (
          error.status === 400 ||
          error.data?.message === 'cryptotop_already_set'
        ) {
          dispatch(
            errorSnack({
              message: `El limite de ASL para el año ${data.cycle} no puede modificarse`,
            })
          )
          setError('cycle', {
            type: 'custom',
            message:
              'Por favor seleccione un año que no tenga un limite de ASL definido',
          })
        } else {
          dispatch(
            errorSnack({
              message: `Ocurrio un error procesando la solicitud, reintente mas tarde`,
            })
          )
        }
      })
  }

  const firstInputRef = useRef(null)

  useEffect(() => {
    /**
     * force focus on inoput field on first render
     */
    if (firstInputRef.current !== null) {
      firstInputRef.current.focus()
    }
    /**
     * Reset form when dialog is closed
     */
    if (!open) {
      reset()
      setSchoolCycleLimitDefined(false)
      setIsBus(false)
    }
  }, [])

  return (
    <>
      <Component onClick={openDialog} {...togglerProps}>
        {children}
      </Component>
      {open && (
        <DialogBase
          title={
            !schoolCycleLimitDefined
              ? 'Definir límite de áctivos para año lectivo'
              : 'Límite de Activos Definido'
          }
          open={open}
          handleClose={closeDialog}
        >
          {!schoolCycleLimitDefined ? (
            <>
              <form
                onSubmit={handleSubmit(onSubmit)}
                className="flex flex-col justify-start items-start gap-8 bg-white tests w-full"
                autoComplete="off"
              >
                <FormControl
                  component="fieldset"
                  error={!!errors.topamounttype}
                  className="w-full flex flex-col items-center p-6 rounded-2xl border border-gray-300 shadow-sm bg-white"
                >
                  <FormLabel
                    component="legend"
                    className="mb-4 text-lg font-semibold text-gray-800 text-center"
                  >
                    Tipo de límite
                  </FormLabel>
                  <Controller
                    name="topamounttype"
                    control={control}
                    defaultValue="e"
                    rules={{ required: 'Este campo es obligatorio' }}
                    render={({ field }) => (
                      <RadioGroup
                        {...field}
                        value={field.value}
                        row
                        className="flex justify-center gap-6"
                        onChange={(e) => {
                          const selected = e.target.value
                          field.onChange(selected)
                          setIsBus(selected === 'b')
                          resetField('cycle')
                        }}
                      >
                        <FormControlLabel
                          value="e"
                          control={<Radio />}
                          label="Estampilla"
                        />
                        <FormControlLabel
                          value="b"
                          control={<Radio />}
                          label="BUS"
                        />
                      </RadioGroup>
                    )}
                  />

                  {errors.topamounttype && (
                    <Typography
                      variant="caption"
                      color="error"
                      className="mt-1"
                    >
                      {errors.topamounttype.message}
                    </Typography>
                  )}
                </FormControl>
                <FieldGrid columns={2} className>
                  <Input
                    key={`cycle-${isBus}`} // ← Esto fuerza que se remonte al cambiar isBus
                    ref={firstInputRef}
                    autoFocus={true}
                    control={control}
                    name={'cycle'}
                    label={'Año lectivo'}
                    validation={{ required: 'Debe seleccionar una opción' }}
                    errors={errors?.cycle}
                    type={'select'}
                    options={cycleOptions}
                    disabled={isLoadingLimits}
                  />
                  <Input
                    name="amount"
                    control={control}
                    helperText="Introduzca un valor mayor a 0 sin decimales"
                    label="Cantidad"
                    defaultValue=""
                    placeholder="0"
                    type="number"
                    errors={errors?.amount}
                    validation={{
                      validate: (value) => {
                        if (value <= 0) {
                          return 'La cantidad de activos debe ser mayor a 0'
                        }
                      },
                    }}
                  />
                </FieldGrid>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Controller
                        name={'confirm'}
                        control={control}
                        render={({ field: props }) => (
                          <Checkbox
                            {...props}
                            checked={props.value}
                            onChange={(e) => props.onChange(e.target.checked)}
                          />
                        )}
                      />
                    }
                    label="Estoy seguro que el límite de activos para año lectivo es correcto y entiendo que no puede modificarse una vez establecido"
                  />
                </FormGroup>
                <div className="flex flex-row justify-center gap-4 w-full pt-">
                  <div className="w-1/2">
                    <SecondaryCta onClick={closeDialog} fullWidth>
                      Cancelar
                    </SecondaryCta>
                  </div>
                  <div className="w-1/2">
                    <PrimaryCta
                      type="submit"
                      fullWidth
                      isSubmitting={isLoading}
                      submittingLabel="Creando"
                      disabled={!watch('confirm')}
                    >
                      Crear
                    </PrimaryCta>
                  </div>
                </div>
              </form>
            </>
          ) : (
            <>
              <Typography
                variant="h1"
                component="div"
                className="mb-8 text-success text-center opacity-50"
              >
                <FontAwesomeIcon icon={faCheckCircle} size="xl" />
              </Typography>
              <Typography variant="body1" className="mb-8 text-center">
                Se ha establecido el limite de{' '}
                <strong>{isBus ? 'BUS' : 'Estampilla'}</strong> para el{' '}
                <strong>año {watch('cycle')}</strong> en{' '}
                <strong>ASL {watch('amount')}</strong>
              </Typography>
              <PrimaryCta onClick={closeDialog} fullWidth>
                Cerrar
              </PrimaryCta>
            </>
          )}
        </DialogBase>
      )}
    </>
  )
}
